// Course service.
//
// Boot behaviour: on first import (in browser), fetch /api/public/courses.
// If the API returns items, they are normalized (defaults filled to match the
// static schema so downstream React components never crash on missing fields)
// and merged with the static COURSES array , DB courses take precedence when
// a slug conflicts, otherwise static courses remain visible.
//
// Consumers can either:
//   1) Use the synchronous helpers (`getAllCourses`, `getCourseBySlug`, etc.)
//      which read the current in-memory cache, OR
//   2) Use the `useCourses()` hook to auto-re-render when data hydrates.

import { useEffect, useState } from "react";
import { COURSES as STATIC_COURSES, OFFERING_CATEGORIES } from "@/data/courses";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const EVT = "zt-courses-changed";

// Normalize a course document so every field the UI reads has a sane default.
// This lets the CMS store partial records without requiring every optional
// SEO / marketing field up front, and keeps the site render-safe.
export function normalizeCourse(c) {
  const title = c.title || "Untitled course";
  const description = c.description || "";
  return {
    title,
    // Big left-hero headline (independent of title, which is on the right card).
    heroHeadline: c.heroHeadline || "",
    slug: c.slug || "",
    category: c.category || "others",
    description,
    longDescription: c.longDescription || description,
    idealFor: c.idealFor || "Learners and professionals",
    format: Array.isArray(c.format) && c.format.length > 0 ? c.format : ["Live"],
    level: c.level || "All Levels",
    duration: c.duration || "Self-paced",
    // Total hours (used in the Outcome section title "... after xx hours").
    hours: c.hours || "",
    schedule: c.schedule || "Rolling cohorts",
    upcomingCohorts: Array.isArray(c.upcomingCohorts) ? c.upcomingCohorts : [],
    zoomRegistrationUrl: c.zoomRegistrationUrl || c.zoom_registration_url || "",
    liveSessionLink: c.liveSessionLink || c.live_session_link || "",
    tags: Array.isArray(c.tags) ? c.tags : [],
    cta: c.cta || "Download Curriculum",
    price: c.price || "$699",
    // Optional struck-through original price shown beside `price`.
    originalPrice: c.originalPrice || "",
    currency: c.currency || "USD",
    seoTitle: c.seoTitle || `${title} | ZaranTech`,
    seoDescription: c.seoDescription || description,
    keywords: Array.isArray(c.keywords) ? c.keywords : [],
    featured: !!c.featured,
    published: c.published !== false,
    // Hidden from the courses list/search/homepage when true, but the
    // course's own /courses/{slug} URL keeps resolving via getCourseBySlug.
    unlisted: !!c.unlisted,
    curriculum: Array.isArray(c.curriculum) ? c.curriculum : [],
    outcomes: Array.isArray(c.outcomes) ? c.outcomes : [],
    // Structured outcome cards [{title, description}] for the Outcome section.
    outcomeItems: Array.isArray(c.outcomeItems) ? c.outcomeItems : [],
    // Structured "Who Is It For" cards [{title, description}].
    audienceItems: Array.isArray(c.audienceItems) ? c.audienceItems : [],
    includedCourses: Array.isArray(c.includedCourses) ? c.includedCourses : [],
    pricing_tiers: Array.isArray(c.pricing_tiers) ? c.pricing_tiers : [],
    videoUrl: c.videoUrl || c.video_url || "",
    videoType: c.videoType || "",
    videoThumbnail: c.videoThumbnail || "",
    videoTitle: c.videoTitle || "",
    videoDescription: c.videoDescription || "",
    videoDuration: c.videoDuration || "",
    highlights: Array.isArray(c.highlights) ? c.highlights : [],
    // Expandable curriculum modules: [{title, content, hands_on_activity}]
    curriculumModules: Array.isArray(c.curriculumModules) ? c.curriculumModules : [],
    // Upsell relationships: slugs of "You might also like" courses
    upsellCourses: Array.isArray(c.upsellCourses) ? c.upsellCourses : [],
    prerequisites: Array.isArray(c.prerequisites) ? c.prerequisites : [],
    curriculumPdf: c.curriculumPdf || c.curriculum_pdf || "",
    // Course-specific FAQs [{q, a}]. Empty => public page falls back to global.
    faqs: Array.isArray(c.faqs) ? c.faqs : [],
    // Technology stack card shown beside the curriculum.
    techStack: Array.isArray(c.techStack) ? c.techStack : [],
    techStackNote: c.techStackNote || "",
    // "About the Instructor" section (no photo).
    instructorName: c.instructorName || "",
    instructorTitle: c.instructorTitle || "",
    instructorBio: c.instructorBio || "",
    instructorHighlights: Array.isArray(c.instructorHighlights) ? c.instructorHighlights : [],
    // Horizontal "explore by role" video row shown above related courses.
    careerVideos: Array.isArray(c.careerVideos) ? c.careerVideos : [],
    careerVideosTitle: c.careerVideosTitle || "",
    // Recommended resources [{slug}] shown as a popup AND a "Free resources"
    // section on this course's detail page (resolved to title/summary/cover).
    resources: Array.isArray(c.resources) ? c.resources : [],
    // Related blog posts [{slug}] shown as a "Related blogs" section,
    // resolved to each post's title/excerpt/cover and linking to /blog/{slug}.
    blogs: Array.isArray(c.blogs) ? c.blogs : [],
    // AEO / GEO optimization. (Course `faqs` above feed the AEO FAQ schema.)
    direct_answer: c.direct_answer || "",
    key_facts: Array.isArray(c.key_facts) ? c.key_facts : [],
    entities: Array.isArray(c.entities) ? c.entities : [],
    sources: Array.isArray(c.sources) ? c.sources : [],
    // Per-course opt-in sections on the detail page.
    show_certified_reviews: !!c.show_certified_reviews,
    show_workday_cert_block: !!c.show_workday_cert_block,
    // Floating "course reel" video widget (distinct from videoUrl above).
    course_reel_video: c.course_reel_video && typeof c.course_reel_video === "object"
      ? { url: c.course_reel_video.url || "", file_url: c.course_reel_video.file_url || "" }
      : null,
    // Preserve any other server-only fields (id, created_at) so admin flows still work.
    id: c.id,
  };
}

// Dedupe by slug: DB course overrides static course with the same slug,
// otherwise static courses stay in the list.
function mergeCourses(dbItems) {
  const bySlug = new Map(STATIC_COURSES.map((s) => [s.slug, s]));
  dbItems.forEach((raw) => {
    const n = normalizeCourse(raw);
    if (n.slug) bySlug.set(n.slug, n);
  });
  return sortCoursesGlobally([...bySlug.values()]);
}

// Global sort rule: within SAP category, "SAP AI Masterclass" must always
// appear first. Other courses keep their current relative order.
function sortCoursesGlobally(courses) {
  const isMasterclass = (c) =>
    c.category === "sap" &&
    (c.slug === "sap-ai-masterclass" ||
      (c.title || "").trim().toLowerCase() === "sap ai masterclass");
  const masterclass = courses.filter(isMasterclass);
  const rest = courses.filter((c) => !isMasterclass(c));
  // Put SAP masterclass entries as the first SAP items; keep other categories intact.
  if (masterclass.length === 0) return rest;
  const firstSapIdx = rest.findIndex((c) => c.category === "sap");
  if (firstSapIdx === -1) return [...masterclass, ...rest];
  return [
    ...rest.slice(0, firstSapIdx),
    ...masterclass,
    ...rest.slice(firstSapIdx),
  ];
}

let _courses = sortCoursesGlobally([...STATIC_COURSES]);
let _hydrated = false;
// True once the initial /api/public/courses fetch has resolved or failed --
// distinct from _hydrated (which only flips when DB data actually merged
// in). Consumers that need to tell "a DB-only course isn't loaded yet" apart
// from "this slug genuinely doesn't exist" should wait for this instead.
let _settled = false;

// Kick off the fetch once, in the browser, non-blocking.
if (typeof window !== "undefined") {
  fetch(`${BACKEND}/api/public/courses`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data && Array.isArray(data.items) && data.items.length > 0) {
        _courses = mergeCourses(data.items);
        _hydrated = true;
      }
    })
    .catch(() => { /* keep static */ })
    .finally(() => {
      _settled = true;
      try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* noop */ }
    });
}

export function getAllCourses() {
  return _courses;
}

export function getCategories() {
  return OFFERING_CATEGORIES;
}

export function getCourseBySlug(slug) {
  return _courses.find((c) => c.slug === slug) || null;
}

export function getCoursesByCategory(categoryId) {
  return _courses.filter((c) => !c.unlisted && c.category === categoryId);
}

// Browsing/search only -- direct-link lookups (getCourseBySlug, getAllCourses)
// deliberately stay unfiltered so an unlisted course's own URL, and any
// explicit slug reference (bundles' includedCourses, upsells, related
// courses), keep resolving normally.
export function searchCourses(query, { tag, category } = {}) {
  const q = (query || "").trim().toLowerCase();
  return _courses.filter((c) => {
    if (c.unlisted) return false;
    if (category && c.category !== category) return false;
    if (tag && tag !== "All" && !(c.tags || []).includes(tag)) return false;
    if (!q) return true;
    const hay = [c.title, c.description, c.idealFor, ...(c.keywords || []), ...(c.tags || [])]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

/**
 * React hook. Returns the current courses array; re-renders when the DB
 * fetch resolves.
 */
export function useCourses() {
  const [, setV] = useState(0);
  useEffect(() => {
    const on = () => setV((x) => x + 1);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  return { courses: _courses, hydrated: _hydrated, settled: _settled };
}
